export const K = 1024; // (K) (kilo) equals (2^10) because computers operate using binary (base-2) logic, not decimal (base-10)

const LOG_1024 = Math.log(K);
const sizes = [
  "Bytes",
  "KB",
  "MB",
  "GB",
  "TB",
  "PB",
  "EB",
  "ZB",
  "YB"
] as const;

type BytesUnitScalingType = (typeof sizes)[number];

/**
 * Byte formatting resolver utility that uses a storage unit closest to the magnitude of the input bytes integer
 */
export function formatBytes(bytes: number | null, decimals = 2): string {
  if (bytes === null) {
    return "";
  }

  if (!+bytes) {
    // For input being 0 or empty, just display "0 Bytes"
    return 0 + " " + sizes[0];
  }

  const dm = decimals < 0 ? 0 : decimals;
  const i = Math.floor(Math.log(bytes) / LOG_1024);
  return (
    Number.parseFloat((bytes / Math.pow(K, i)).toFixed(dm)) + " " + sizes[i]
  );
}

/**
 * Returns the scaling factor for the byte unit
 * @param unit - a "Bytes", "KB", "MB", "GB", etc. compute measurement unit
 */
function getBytesUnitScaling(unit: BytesUnitScalingType): number {
  return sizes.indexOf(unit);
}

/**
 * Parses result of `formatBytes()` into a discrete structure of `[bytes value, bytes unit, bytes scaling factor]`
 */
function parseFormatBytes(
  formatBytesResult: ReturnType<typeof formatBytes>
): [number, BytesUnitScalingType, number] {
  const [value, unit] = formatBytesResult.split(" ");
  const bytesUnitScaling: BytesUnitScalingType =
    (unit as BytesUnitScalingType) ?? sizes[0];
  return [
    Number.parseFloat(value ?? "0"),
    bytesUnitScaling,
    getBytesUnitScaling(bytesUnitScaling)
  ];
}

/**
 * Converts a formatBytes result back into a nominal number of bytes
 * Useful for rounding larger values that have scaled up units such as GB or TB to their nearest whole number
 */
export function convertToBytes(
  formatBytesResult: ReturnType<typeof formatBytes>
) {
  const [bytesNumberValue, , bytesScalingFactor] =
    parseFormatBytes(formatBytesResult);
  return bytesNumberValue * Math.pow(K, bytesScalingFactor);
}
